import {useEffect, useState} from 'react';
import {useForm} from "react-hook-form";
import apiClient from "../../../../utils/apiClient";
import FormSelect from "../../../forms/form-control/FormSelect";
import FormSubmitButton from "../../../forms/form-control/FormSubmitButton";
import {updateStateValue} from "../../../../utils/stateHelpers";
import {isEmpty} from "lodash";
import FormInputDate from "../../../forms/form-control/FormInputDate";
import moment from "moment";
import FormSearchableSelect from '../../../forms/form-control/FormSearchableSelect';

/**
 * Filter component for average downtime by toolpusher.
 * @returns {JSX.Element}
 * @constructor
 */
const AverageDowntimeByToolpusherFilters = (prop) => {
    const {register, handleSubmit, errors, setValue, getValues, clearErrors, control, watch} = useForm();
    const [toolpushers, setToolpushers] = useState([]);
    const [rigs, setRigs] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [financialPeriods, setFinancialPeriods] = useState([]);
    const {data, setData, setFilters} = prop;
    const [endDate, setEndDate] = useState(0);
    const jobId = watch('job_id');
    const dateFrom = watch('date_from');
    const dateTo = watch('date_to');

    // submit handler for report filter
    const onSubmit = (data) => {
        updateStateValue(setFilters, 'isLoading', true);
        updateStateValue(setFilters, 'isSubmitted', true);

        apiClient
            .post('getAverageDowntimeByToolpusher', { 
                ...data,
                toolpusher_id: data.toolpusher_id.value,
            })
            .then(response => {
                if (isEmpty(response.data)) {
                    setData([]);
                    return;
                }
                setData(response.data);
            })
            .then(function () {
                updateStateValue(setFilters, 'isLoading', false);
        });
    }

    // get list of dates
    useEffect(() => {
        apiClient
            .get('getActiveRigs')
            .then(response => {
                const rigItems = response.data.rigs.map(
                    ({id, name}) => ({id: id, name: name})
                );
                setRigs(rigItems);
            });

        apiClient
            .get('getCurrentPayPeriod')
            .then(response => {
                let year = Number(moment(response.data.end_date, 'MM-DD-YYYY').format('YYYY'));
                setEndDate(year);
            });

        // get FinancialPeriods
        apiClient
            .get('getFinancialPeriods')
            .then(response => {
                setFinancialPeriods(
                    response.data.map(
                        ({id, name, is_current}) => ({
                            id: id, name: name + (is_current ? ' (Current)' : '')
                        })
                    )
                );
            });
        
        // set yesterday date for date range components
        setValue('date_from', moment().format("YYYY-MM-DD"));
        setValue('date_to', moment().add(2, "month").format("YYYY-MM-DD"));
    }, [setValue, endDate]);

    useEffect(() => {
        setValue('toolpusher_id', null);
        setToolpushers([]);
        if (!isEmpty(jobId) && !isEmpty(dateFrom) && !isEmpty(dateTo)) {
            apiClient
                .post('getToolpushersByJobAndDate', {
                    job_id: jobId,
                    date_from: dateFrom,
                    date_to: dateTo,
                })
                .then(response => {
                    setToolpushers(
                        response.data.map(
                            ({id, full_name}) => ({value: id, label: full_name})
                        )
                    );
                });
        }
    }, [jobId, dateFrom, dateTo, setValue])

    const setDateByMonthlyPeriod = (value) => {
        if (value) {
            apiClient
                .post('getFinancialPeriodById', {'id': value})
                .then(response => {
                    let startDate = response.data.start_date;
                    let endDate = response.data.end_date;
                    document.getElementById('date_from').value = startDate;
                    document.getElementById('date_to').value = endDate;
                    clearErrors('date_from');
                    clearErrors('date_to');
            });
        }
    }

    // rig selection handler
    const rigSelectionHandler = (e) => {
        setValue('job_id', null);
        setValue('toolpusher_id', null);
        setJobs([]);
        setToolpushers([]);

        if (getValues('rig_id') !== '') {
            // get Rigs by Discipline
            apiClient
                .post('getActiveJobsByRigId', {'rig_id': e.target.value})
                .then(response => {
                    const jobItems = response.data.jobs.map(
                        ({id, job_number}) => ({id: id, name: job_number})
                    );
                    setJobs(jobItems);
                });
        }
    }

    return (
        <form
            className="needs-validation s-filters-form"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="s-filters">
                <FormSelect
                    id="rig_id"
                    label="Rig"
                    className="s-filter"
                    options={rigs}
                    onChange={rigSelectionHandler}
                    register={register({required: "required"})}
                    errors={errors}
                />
                <FormSelect
                    id="job_id"
                    label="Job"
                    options={jobs}
                    register={register({required: "required"})}
                    errors={errors}
                />
                <FormSearchableSelect
                    id="toolpusher_id"
                    label="Tool Pusher"
                    className="col-xs-12 col-sm-6 col-md-3"
                    options={toolpushers}
                    rules={{required: "required"}}
                    customStyles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                    control={control}
                    errors={errors}
                />
                <FormSelect
                    id="financial_period_id"
                    label="Monthly Data Period"
                    className="s-filter"
                    options={financialPeriods}
                    register={register}
                    onChange={(e) => {
                        setValue('pay_period_id', '');
                        setDateByMonthlyPeriod(e.target.value);
                        setValue('date', '');
                    }}
                    errors={errors}
                />
                <FormInputDate
                    id="date_from"
                    label="From"
                    className="s-filter"
                    onChange={() => {
                        setValue('pay_period_id', '');
                        setValue('financial_period_id', '');
                        setValue('date', '');
                        if (isEmpty(getValues('date_to'))) {
                            setValue('date_to', getValues('date_from'));
                        }

                    }}
                    register={register({required: "required"})}
                    errors={errors}
                />
                <FormInputDate
                    id="date_to"
                    label="To"
                    className="s-filter"
                    onChange={() => {
                        setValue('pay_period_id', '');
                        setValue('financial_period_id', '');
                        setValue('date', '');
                    }}
                    register={register({required: "required"})}
                    errors={errors}
                />
                
                <FormSubmitButton
                    label="Show"
                    className="s-button s-button-xl"
                />
                
                {/* For mobile view */}
                {!isEmpty(data) ? (
                    <div className="s-buttons s-justify-between">
                        <FormSubmitButton
                            label="Show"
                            className="s-button"
                        />
                    </div>
                ) : (
                    <div className="s-buttons s-justify-end">
                        <FormSubmitButton
                            label="Show"
                            className="s-button"
                        />
                    </div>
                )}
            </div>
        </form>
    );
}

export default AverageDowntimeByToolpusherFilters;
